import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import AiReplyAvatar from './AiReplyAvatar';
import AssistantErrorBubble from './AssistantErrorBubble';
import {
  highlightParts,
  labelLines,
  messageScrollAction,
  preservedScrollTop,
  scrollTopAfterResize,
  scrollTopForMessage,
  searchAnswers,
  shouldLoadOlder,
  threadItems,
} from '../shared/assistantThread';

const EXAMPLES = [
  'How often should I change my HVAC filter?',
  "What's the best way to winterize my sprinkler system?",
  'How do I maintain my pool during summer?',
];

function IconButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        width: '44px',
        height: '44px',
        borderRadius: '999px',
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <circle cx="6" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="18" cy="12" r="1.6" />
    </svg>
  );
}

function Highlighted({ text, query, active }) {
  const parts = highlightParts(text, query);
  return parts.map((part, index) => (
    part.match ? (
      <mark
        key={index}
        style={{
          background: active ? '#f59e0b' : '#fde68a',
          color: '#1f2937',
          borderRadius: '0.2rem',
          padding: '0 0.1rem',
        }}
      >
        {part.text}
      </mark>
    ) : <span key={index}>{part.text}</span>
  ));
}

export default function AssistantThread({
  intro = 'Ask about your home. Your chats stay here.',
  messages,
  loading,
  loadingOlder,
  hasOlder,
  onLoadOlder,
  onSearch,
  onReveal,
  onStartFresh,
  onRetry,
  onExample,
  aiLoading,
  colors,
  isMobile,
  brandColor,
  renderExtra,
  children,
  now,
}) {
  const palette = colors || {};
  const text = palette.text || '#111827';
  const muted = palette.textSecondary || '#6b7280';
  const surface = palette.surface || '#ffffff';
  const alt = palette.surfaceAlt || '#f3f4f6';
  const border = palette.border || '#e5e7eb';
  const accent = brandColor || palette.primary || '#59c4c4';
  const scrollerRef = useRef(null);
  const stickRef = useRef(true);
  const heightRef = useRef(0);
  const topRef = useRef(0);
  const loadingOlderRef = useRef(false);
  const didInitialScroll = useRef(false);
  const following = useRef(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remoteHits, setRemoteHits] = useState(null);
  const [activeHit, setActiveHit] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmFresh, setConfirmFresh] = useState(false);
  const items = threadItems(messages, now || new Date());
  const localHits = searchAnswers(messages, query);
  const hits = remoteHits || localHits;
  const active = hits[activeHit] || null;
  const activeMessageId = active ? (active.messageId || `${active.id}:assistant`) : '';

  const scrollLogToEnd = (node, behavior) => {
    following.current = true;
    const top = node.scrollHeight;
    if (behavior === 'smooth' && typeof node.scrollTo === 'function') {
      try {
        node.scrollTo({ top, behavior: 'smooth' });
        return;
      } catch (error) {
        // Older WebKit rejects the options object.
      }
    }
    node.scrollTop = top;
  };

  useLayoutEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const action = messageScrollAction({
      stuck: stickRef.current,
      loadingOlder: loadingOlderRef.current,
      initial: !didInitialScroll.current,
    });
    if (action === 'preserve') {
      node.scrollTop = preservedScrollTop(topRef.current, heightRef.current, node.scrollHeight);
      loadingOlderRef.current = false;
      topRef.current = node.scrollTop;
      return;
    }
    if (action === 'stay') return;
    scrollLogToEnd(node, action === 'smooth' ? 'smooth' : 'auto');
    if (!loading) didInitialScroll.current = true;
    topRef.current = node.scrollTop;
  }, [messages, aiLoading, loading]);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return undefined;
    // The keyboard and the photo card shrink this list. Anchoring is off,
    // so keep the newest line above the composer when the reader is at the bottom.
    const keepLatestInView = () => {
      const current = scrollerRef.current;
      if (!current) return;
      const next = scrollTopAfterResize({
        stuck: stickRef.current,
        scrollTop: current.scrollTop,
        scrollHeight: current.scrollHeight,
        clientHeight: current.clientHeight,
      });
      if (next === current.scrollTop) return;
      current.scrollTop = next;
      topRef.current = next;
    };
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(keepLatestInView);
      observer.observe(node);
    }
    const viewport = window.visualViewport;
    if (viewport && typeof viewport.addEventListener === 'function') {
      viewport.addEventListener('resize', keepLatestInView);
    }
    return () => {
      if (observer) observer.disconnect();
      if (viewport && typeof viewport.removeEventListener === 'function') {
        viewport.removeEventListener('resize', keepLatestInView);
      }
    };
  }, []);

  useEffect(() => {
    if (!activeMessageId) return undefined;
    const log = scrollerRef.current;
    if (!log) return undefined;
    const nodes = log.querySelectorAll('[data-message-id]');
    let node = null;
    for (let index = 0; index < nodes.length; index += 1) {
      if (nodes[index].getAttribute('data-message-id') === activeMessageId) {
        node = nodes[index];
        break;
      }
    }
    if (!node || !log.getBoundingClientRect || !node.getBoundingClientRect) return undefined;
    const logBox = log.getBoundingClientRect();
    const box = node.getBoundingClientRect();
    following.current = false;
    log.scrollTop = scrollTopForMessage({
      scrollTop: log.scrollTop,
      logTop: logBox.top,
      logHeight: log.clientHeight,
      nodeTop: box.top,
      nodeHeight: box.height,
    });
    const distance = log.scrollHeight - log.scrollTop - log.clientHeight;
    stickRef.current = distance < 80;
    topRef.current = log.scrollTop;
    return undefined;
  }, [activeMessageId]);

  const noteUserScroll = () => {
    following.current = false;
  };

  const onScroll = () => {
    const node = scrollerRef.current;
    if (!node) return;
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    topRef.current = node.scrollTop;
    if (!following.current) {
      stickRef.current = distance < 80;
    } else if (distance < 80) {
      following.current = false;
      stickRef.current = true;
    }
    if (shouldLoadOlder(node.scrollTop, hasOlder, loadingOlder)) {
      heightRef.current = node.scrollHeight;
      loadingOlderRef.current = true;
      if (onLoadOlder) onLoadOlder();
    }
  };

  const runSearch = async (event) => {
    if (event) event.preventDefault();
    const found = searchAnswers(messages, query);
    setRemoteHits(found);
    setActiveHit(0);
    if (query.trim().length < 2) return;
    if (onSearch) {
      try {
        const remote = await onSearch(query);
        if (Array.isArray(remote) && remote.length) {
          setRemoteHits(remote);
          if (onReveal) await onReveal(remote[0]);
          return;
        }
      } catch (error) {
        // The messages already on screen can still be searched.
      }
    }
    if (found[0] && onReveal) await onReveal(found[0]);
  };

  const stepHit = (direction) => {
    if (!hits.length) return;
    const next = (activeHit + direction + hits.length) % hits.length;
    setActiveHit(next);
    const hit = hits[next];
    if (hit && onReveal) onReveal(hit);
  };

  return (
    <div
      data-assistant-thread="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      <div data-assistant-header="true" style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ color: text, fontSize: '1.2rem', fontWeight: 500, margin: '0 0 0.25rem' }}>
            AI Home Assistant
          </h3>
          <p style={{ color: muted, fontSize: '0.95rem', margin: 0 }}>
            {intro}
          </p>
        </div>
        <div style={{ display: 'flex', flexShrink: 0 }}>
          <IconButton label="Search answers" onClick={() => { setSearchOpen((open) => !open); setMenuOpen(false); }}>
            <SearchIcon />
          </IconButton>
          <div style={{ position: 'relative' }}>
            <IconButton label="Chat options" onClick={() => { setMenuOpen((open) => !open); setSearchOpen(false); }}>
              <MoreIcon />
            </IconButton>
            {menuOpen ? (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: '44px',
                  right: 0,
                  zIndex: 5,
                  minWidth: '180px',
                  background: surface,
                  border: `1px solid ${border}`,
                  borderRadius: '0.75rem',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: '0.25rem',
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); setConfirmFresh(true); }}
                  style={{
                    width: '100%',
                    minHeight: '44px',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    color: text,
                    fontSize: '1rem',
                    borderRadius: '0.6rem',
                    cursor: 'pointer',
                    padding: '0.5rem 0.75rem',
                  }}
                >
                  Start fresh
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {searchOpen ? (
        <form onSubmit={runSearch} style={{ flex: '0 0 auto', display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
          <input
            aria-label="Find an old answer"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setRemoteHits(null); setActiveHit(0); }}
            placeholder="Find an old answer"
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              minHeight: '44px',
              borderRadius: '999px',
              border: `1px solid ${border}`,
              background: surface,
              color: text,
              fontSize: '1rem',
              padding: '0.5rem 1rem',
            }}
          />
          <button type="submit" style={{ minHeight: '44px', border: 'none', background: 'transparent', color: accent, fontWeight: 650, cursor: 'pointer' }}>
            Find
          </button>
          {hits.length > 1 ? (
            <>
              <IconButton label="Previous answer" onClick={() => stepHit(-1)}>↑</IconButton>
              <IconButton label="Next answer" onClick={() => stepHit(1)}>↓</IconButton>
            </>
          ) : null}
        </form>
      ) : null}
      {searchOpen && query.trim().length >= 2 && hits.length === 0 ? (
        <p style={{ flex: '0 0 auto', color: muted, margin: '0 0 0.75rem', fontSize: '0.95rem' }}>No answers match that.</p>
      ) : null}

      {/*
        Block layout on purpose. A flex column with overflow-y:auto jumps
        back to the top in WebKit once the reader reaches the end, which
        brought the "AI Home Assistant" heading back into view.
      */}
      <div
        ref={scrollerRef}
        role="log"
        aria-label="Chat"
        onScroll={onScroll}
        onWheel={noteUserScroll}
        onTouchStart={noteUserScroll}
        onPointerDown={noteUserScroll}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          overflowAnchor: 'none',
          touchAction: 'pan-y',
          padding: '1rem',
          background: alt,
          borderRadius: isMobile ? '1.25rem' : '0.5rem',
        }}
      >
        {loadingOlder ? (
          <p style={{ textAlign: 'center', color: muted, fontSize: '0.9rem', margin: '0 0 0.75rem' }}>
            Loading older messages...
          </p>
        ) : null}
        {loading ? (
          <p style={{ textAlign: 'center', color: muted, margin: '2rem 0' }}>Loading your chat...</p>
        ) : null}
        {!loading && (!messages || messages.length === 0) ? (
          <div style={{ textAlign: 'center', color: muted, padding: '2rem 0.5rem' }}>
            <p style={{ margin: '0 0 1rem', color: text }}>Nothing here yet.</p>
            <p style={{ margin: '0 0 0.75rem', fontWeight: 650, color: text }}>Try asking me:</p>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => { if (onExample) onExample(example); }}
                  style={{
                    padding: '0.75rem 1rem',
                    background: surface,
                    border: `1px solid ${border}`,
                    borderRadius: isMobile ? '1.25rem' : '0.5rem',
                    color: muted,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    minHeight: '44px',
                  }}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {items.map((item) => {
          if (item.type === 'day') {
            return (
              <div key={item.id} data-day-separator={item.label} style={{ textAlign: 'center', color: muted, fontSize: '0.8rem', fontWeight: 650, margin: '0.25rem 0 0.75rem' }}>
                {item.label}
              </div>
            );
          }
          if (item.type === 'fresh') {
            return (
              <div key={item.id} data-fresh-start="true" style={{ textAlign: 'center', color: muted, fontSize: '0.85rem', margin: '0.25rem 0 0.75rem' }}>
                Started fresh
              </div>
            );
          }
          const msg = item.message;
          if (msg.error) {
            return (
              <AssistantErrorBubble
                key={msg.id}
                message={msg.content}
                onRetry={aiLoading ? undefined : () => onRetry && onRetry(msg)}
                colors={palette}
              />
            );
          }
          const mine = msg.role === 'user';
          const isActive = activeMessageId && msg.id === activeMessageId;
          const lines = msg.role === 'assistant' ? labelLines(msg.label) : [];
          return (
            <div
              key={msg.id}
              data-message-id={msg.id}
              style={{
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: mine ? 'flex-end' : 'flex-start',
                gap: '0.5rem',
                alignItems: 'flex-end',
              }}
            >
              {msg.role === 'assistant' ? <AiReplyAvatar /> : null}
              <div style={{
                maxWidth: '75%',
                padding: '1rem 1.25rem',
                borderRadius: mine ? '1.25rem 1.25rem 0.25rem 1.25rem' : '1.25rem 1.25rem 1.25rem 0.25rem',
                background: mine ? accent : surface,
                color: mine ? '#ffffff' : text,
                boxShadow: isActive ? '0 0 0 3px #f59e0b' : (mine ? '0 2px 8px rgba(89, 196, 196, 0.3)' : '0 2px 8px rgba(0,0,0,0.08)'),
                border: msg.role === 'assistant' ? `1px solid ${border}` : 'none',
                fontSize: '1rem',
                lineHeight: 1.5,
                overflowWrap: 'break-word',
              }}
              >
                {msg.imageUrl ? (
                  <img
                    src={msg.imageUrl}
                    alt={msg.image || 'Photo you sent'}
                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '0.75rem', marginBottom: '0.5rem', objectFit: 'contain', display: 'block' }}
                  />
                ) : null}
                {msg.image && !msg.imageUrl ? (
                  <div style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: '0.5rem' }}>Photo: {msg.image}</div>
                ) : null}
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  <Highlighted text={msg.content} query={searchOpen ? query : ''} active={isActive} />
                </div>
                {lines.length ? (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px solid ${border}`, fontSize: '0.9rem' }}>
                    <div style={{ fontWeight: 650, marginBottom: '0.35rem' }}>From the label</div>
                    {lines.map((line) => (
                      <div key={line.key}>
                        {line.title}: <Highlighted text={line.value} query={searchOpen ? query : ''} active={isActive} />
                      </div>
                    ))}
                  </div>
                ) : null}
                {renderExtra ? renderExtra(msg) : null}
                <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.5rem' }}>
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
              </div>
            </div>
          );
        })}
        {aiLoading ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
            <AiReplyAvatar />
            <div style={{ padding: '1rem 1.25rem', borderRadius: '1.25rem 1.25rem 1.25rem 0.25rem', background: surface, border: `1px solid ${border}` }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[0, 0.2, 0.4].map((delay) => (
                  <div key={delay} style={{ width: '8px', height: '8px', borderRadius: '50%', background: accent, animation: `pulse 1.4s ease-in-out ${delay}s infinite` }} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {children ? (
        <div data-assistant-composer="true" role="group" aria-label="Message composer" style={{ flex: '0 0 auto', paddingTop: '0.75rem' }}>
          {children}
        </div>
      ) : null}
      {confirmFresh ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="start-fresh-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 4500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div style={{ width: 'min(100%, 420px)', background: surface, color: text, borderRadius: '1rem', padding: '1.25rem', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
            <h4 id="start-fresh-title" style={{ margin: '0 0 0.5rem', fontSize: '1.15rem' }}>Start fresh?</h4>
            <p style={{ margin: '0 0 1rem', lineHeight: 1.45 }}>
              Your old messages stay in this chat. New questions won't use them.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setConfirmFresh(false)}
                style={{ minHeight: '44px', padding: '0.5rem 1rem', borderRadius: '0.75rem', border: `1px solid ${border}`, background: surface, color: text, cursor: 'pointer' }}
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmFresh(false);
                  if (onStartFresh) onStartFresh();
                }}
                style={{ minHeight: '44px', padding: '0.5rem 1rem', borderRadius: '0.75rem', border: 'none', background: accent, color: '#ffffff', fontWeight: 650, cursor: 'pointer' }}
              >
                Start fresh
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
